import React, { useState } from 'react'
import { CheckCircle2, Monitor, Terminal } from 'lucide-react'

import { cn } from '../lib/utils'
import { AGENT_TOOLS, type AgentConnectionKind, type AgentToolDef } from '../lib/agentTools'
import { AgentToolIcon } from './brand/AgentToolIcon'
import { Badge } from './ui/badge'
import { Button } from './ui/button'

// ============================================================================
// AgentConnectGrid
//
// Extracted 2026-10-08 from WelcomeModal.tsx's step 6 ("Connect your
// agents") so the onboarding wizard and the Agents page's "+ Add Agent"
// sidebar (TeamAgents.tsx) share one implementation instead of two that
// drift apart -- exactly what happened to WelcomeModal's AGENT_TOOLS vs.
// AllowedAgentsConfig.tsx's separate catalog (still unreconciled; out of
// scope for this change).
//
// Deliberately a flat terminal/desktop split with no provider-selection
// step on top: every tool here is shown with its own icon/name (via
// AgentToolIcon), and the connection *mechanism* -- paste-a-command hook
// install vs. app/browser pairing -- tracks `kind`, not vendor. A
// provider-first picker (Anthropic vs. OpenAI, say) would still need
// Cursor to live somewhere (it's Anysphere, not either), and would fragment
// today's 4-tool catalog into mostly single-item per-provider buckets --
// see the 2026-10-08 conversation this design came out of.
//
// `connectedClientNames` and `onConnect` are owned by the caller (not this
// component) because "connected" is answered differently in each host:
// WelcomeModal polls team-wide client_name values while its step 6 is
// showing; TeamAgents.tsx already has the team's agent rows loaded for its
// own grid and derives the same Set from that, no extra query needed.
// ============================================================================

interface AgentConnectGridProps {
  tools?: AgentToolDef[]
  connectedClientNames: Set<string>
  onConnect: (tool: AgentToolDef) => void
  defaultTab?: AgentConnectionKind
  className?: string
}

export function AgentConnectGrid({
  tools = AGENT_TOOLS,
  connectedClientNames,
  onConnect,
  defaultTab = 'terminal',
  className,
}: AgentConnectGridProps) {
  const [tab, setTab] = useState<AgentConnectionKind>(defaultTab)

  const isConnected = (tool: AgentToolDef) => connectedClientNames.has(tool.name.toLowerCase())
  const connectedCount = tools.filter(isConnected).length

  return (
    <div className={cn('space-y-4', className)}>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setTab('terminal')}
          className={cn(
            'flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
            tab === 'terminal'
              ? 'border-foreground bg-card text-foreground'
              : 'border-border bg-muted/40 text-muted-foreground hover:border-foreground/30'
          )}
        >
          <Terminal className="h-4 w-4" />
          Terminal agents ({tools.filter((t) => t.kind === 'terminal').length})
        </button>
        <button
          type="button"
          onClick={() => setTab('desktop')}
          className={cn(
            'flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
            tab === 'desktop'
              ? 'border-foreground bg-card text-foreground'
              : 'border-border bg-muted/40 text-muted-foreground hover:border-foreground/30'
          )}
        >
          <Monitor className="h-4 w-4" />
          Desktop agents ({tools.filter((t) => t.kind === 'desktop').length})
        </button>
      </div>

      <p className="text-sm text-muted-foreground">
        {tab === 'terminal'
          ? 'Install in your own terminal. Set up again to reconnect or add another computer.'
          : 'Desktop app connections are coming soon.'}
      </p>

      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
        {connectedCount} agent{connectedCount === 1 ? '' : 's'} connected
      </p>

      <div className="space-y-2">
        {tools
          .filter((tool) => tool.kind === tab)
          .map((tool) => {
            const connected = isConnected(tool)
            return (
              <div
                key={tool.id}
                className="flex min-h-14 items-center justify-between gap-3 rounded-xl border border-border p-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <AgentToolIcon clientName={tool.name} className="h-8 w-8 shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-foreground">{tool.name}</span>
                      <Badge variant="outline" className="shrink-0 text-[10px] uppercase">
                        {tool.badge}
                      </Badge>
                    </div>
                    <p className="truncate text-sm text-muted-foreground">{tool.description}</p>
                  </div>
                </div>
                {connected && tool.available ? (
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Badge variant="success" className="gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Connected</Badge>
                    <Button size="sm" variant="outline" onClick={() => onConnect(tool)}>Set up again</Button>
                  </div>
                ) : tool.available ? (
                  <Button size="sm" onClick={() => onConnect(tool)} className="shrink-0">
                    Connect
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" disabled title="Coming soon" className="shrink-0">
                    Coming soon
                  </Button>
                )}
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default AgentConnectGrid
